import type { State } from 'src/replicant/State';
import * as Pet from '../pet';
import * as Fate from './';
import {
  hasPermanentStatusEffect,
  isSafeTime,
  getAwakeDuration,
  getSleepingDuration,
  getStatusEffectStartTime,
  shouldBeAsleep,
} from './fate.getters';
import * as Visit from '../visit';
import {
  sickCooldownInterval,
  dyingCooldownInterval,
  toothDecayCooldownInterval,
  scaredCooldownInterval,
  sleepingPeriod,
  deterministicSickStress,
} from './fate.ruleset';
import { getJSTDate } from '../utils/time';
import { duration } from '../utils/duration';
import { getPetSpeciesBucket, getStressValue } from '../pet/pet.getters';
import features, { Features } from '../ruleset/features';
import { getBuruFriendsCount } from '../friends/friends.getters';

/**
 * Detect if pet will want to invite a friend with given input params
 */
export const shouldWantToInvite = (
  state: State,
  random: number,
  now: number,
  features: Features,
) => {
  // A/B 0469: V2 dispatch (awake-time gate + cooldown floor)
  if (features.wantToInviteV2) {
    return shouldWantToInviteV2(state, random, now);
  }

  // Feature not enabled
  if (!features.wantToInvite) {
    return false;
  }

  const { getCurrentEvolution } = Pet.getters;

  // Pet not available
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) {
    return false;
  }

  // Unlocks after 2nd evolution (kid→youth)
  if (state.pet.evolutions.length <= 2) {
    return false;
  }

  const lastInviteTimestamp =
    state.pet.statusEffects.wantToInvite.lastUpdatedAt;

  const timeSinceLastInvite = getAwakeDuration(state, lastInviteTimestamp, now);

  const friendCount = getBuruFriendsCount(state);

  return Pet.ruleset.shouldWantToInvite(
    timeSinceLastInvite,
    random,
    friendCount,
  );
};

/**
 * A/B 0468 V2 invite gate. Two paths:
 *
 *  1. First nudge: deterministic — fires as soon as 10 minutes of cumulative
 *     awake time elapses since pet creation. Restricted to true D0 users
 *     (first pet ever, still in baby stage, never invited). Existing users
 *     on V2 ramp do NOT qualify even though their `lastWantToInviteFireAt`
 *     defaults to 0; they route to Path 2 instead.
 *  2. Recurrence (everyone else): fires after the friend-count-scaled
 *     cooldown floor has elapsed in awake time, then the existing
 *     probability ramp from 0440 decides each 5-min loop step.
 */
const shouldWantToInviteV2 = (state: State, random: number, now: number) => {
  const { getCurrentEvolution, getPetCreatedAt } = Pet.getters;

  // Pet must exist (no nudge while pet is being spawned).
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) return false;
  const petCreatedAt = getPetCreatedAt(state);
  if (!petCreatedAt) return false;

  // `lastWantToInviteFireAt` is written ONLY when the wantToInvite status
  // effect transitions false → true via `setStatusEffect` (see
  // pet.modifiers). 0 = never fired. Unlike `statusEffects.wantToInvite.
  // lastUpdatedAt`, this field is NOT touched by `syncPermanentStatusEffects`
  // pollution writes, so it's the authoritative sentinel for the V2 gate.
  const lastInviteTimestamp = state.pet.lastWantToInviteFireAt;
  const firstNudgeGate = duration({ minutes: 10 });
  const neverInvited = lastInviteTimestamp === 0;

  // Path 1 eligibility — true D0 only. `petRaised === 0` means no
  // death/graduation/duo-debut has happened (incremented only by `spawnPet`,
  // not by `createFirstPet`). Combined with a single baby evolution this
  // matches a brand-new account on its first pet. Without this guard,
  // existing users would all satisfy `neverInvited` on V2 ramp (the
  // sentinel defaults to 0) and trigger Path 1 immediately because
  // `awakeSinceCreation` is huge for old pets.
  const isFirstPetBabyD0 =
    state.petRaised === 0 &&
    state.pet.evolutions.length === 1 &&
    currentEvolution.stage === 'baby';

  // Friend count drives the cooldown floor in the recurrence path; the spec
  // table is at pet.ruleset.ts (0–2 = 24h, 3–4 = 72h, 5+ = 168h).
  const friendCount = getBuruFriendsCount(state);

  // ── Path 1 — first nudge ────────────────────────────────────────────────
  // The D0 case. Spec § "Trigger Gate" defines this as a deterministic
  // gate — fires as soon as 10 min of cumulative awake time elapses, no
  // probability roll. Reason: median D0 session is 8–12 min, so a
  // probabilistic ramp (~0.5%/step at 10 min for 0 friends) would miss most
  // first sessions and defeat the spec's goal of D0 visibility. Path 1 is
  // intended ONLY for true new users — `isFirstPetBabyD0` keeps existing
  // users (whose sentinel also defaults to 0 on V2 ramp) out.
  if (neverInvited && isFirstPetBabyD0) {
    const awakeSinceCreation = getAwakeDuration(state, petCreatedAt, now);
    return awakeSinceCreation >= firstNudgeGate;
  }

  // ── Path 2 — recurrence ─────────────────────────────────────────────────
  // After the first nudge, gate every subsequent fire by a friend-count-
  // scaled cooldown floor measured in awake time. Awake-time (not wall-
  // clock) keeps the cadence consistent across nights / long offline gaps,
  // matching how the spec describes "frequency" for active users.
  //
  // Anchor note: `lastInviteTimestamp` is `state.pet.lastWantToInviteFireAt`,
  // which is written ONLY on a real false→true transition in
  // `setStatusEffect` (i.e. when the schedule entry matures into a fire),
  // plus a one-time fallback in `resolveWantInvite` that promotes the
  // first dismiss to a "fire time" for catch-up users whose initial
  // value was 0. Once set it stays put across user dismissals — the
  // cooldown is therefore strictly anchored to the LAST FIRE, matching
  // spec's "timeSinceLastInvite" exactly.
  const awakeSinceLastInvite = getAwakeDuration(
    state,
    lastInviteTimestamp,
    now,
  );

  // Cooldown floor table (from pet.ruleset): 0–2 friends → 24h, 3–4 → 72h,
  // 5+ → 168h. Users with more friends already get social activity, so the
  // nudge needs less frequent reinforcement. Skip the probability roll
  // entirely until the floor has elapsed in awake-time.
  const cooldown = Pet.ruleset.getInviteCooldownFloor(friendCount);
  if (awakeSinceLastInvite < cooldown) return false;

  // Probability ramp inherited from 0440. Per the formula in pet.ruleset:
  //
  //   P = multiplier × 0.0001 × awakeSinceLastInvite (in minutes)
  //   multiplier = 5× (0 friends) / 3× (1–2) / 2× (3–4) / 1× (5+)
  //
  // P is 0 right after the cooldown clears and grows linearly with elapsed
  // awake-time, so a user who's been idle longer is more likely to fire on
  // any given 5-min loop step. The fewer-friends multiplier biases the
  // experience toward users who'd benefit most from social nudging.
  // `random` is the per-step PRNG draw; `random < P` is the fire condition.
  return Pet.ruleset.shouldWantToInvite(
    awakeSinceLastInvite,
    random,
    friendCount,
  );
};

/**
 * A/B 0468 V2: compute the wall-clock timestamp at which `wantToInvite`
 * should fire, given the loop iteration's `now` and current features.
 * Called by the fate loop immediately after `shouldWantToInvite` returns
 * true.
 *
 * **V2 Path 1 (true D0 first nudge — `lastWantToInviteFireAt === 0` AND
 * the same `isFirstPetBabyD0` guard as `shouldWantToInviteV2`):** returns
 * a STABLE timestamp computed from pet creation time — `petCreatedAt + 10
 * min` if the pet is awake at creation (initial baby or wake hours), or
 * `nextWakeUp + 10 min` if it was created during the JST sleep window
 * AND isn't an initial baby (`shouldBeAsleep` returns true). Because the
 * schedule timestamp doesn't depend on the loop's `now`, care actions
 * can't push the D0 nudge forward across the schedule wipe-and-rebuild
 * cycle.
 *
 * **V1 or V2 Path 2 (probability-based, including catch-up users whose
 * sentinel defaults to 0 on V2 ramp):** returns the loop's `now` so the
 * schedule reflects the stochastic per-step decision (consistent with
 * how every other status effect's schedule works). The Path 1 guard must
 * match the gate in `shouldWantToInviteV2` — otherwise a non-D0 user
 * whose `lastWantToInviteFireAt` is 0 would have their schedule pinned
 * to an ancient `petCreatedAt + 10min` (already past), guaranteeing
 * instant re-fire on the next loop step.
 */
export const getWantToInviteScheduleTimestamp = (
  state: State,
  now: number,
  features: Features,
): number => {
  if (!features.wantToInviteV2) return now;

  const lastInviteTimestamp = state.pet.lastWantToInviteFireAt;
  if (lastInviteTimestamp !== 0) return now;

  const petCreatedAt = Pet.getters.getPetCreatedAt(state);
  if (!petCreatedAt) return now;

  // Must mirror the `isFirstPetBabyD0` guard in `shouldWantToInviteV2`.
  // Both functions answer the same routing question — "is this a true D0
  // user?" — and a non-D0 user that somehow reaches this branch with
  // `lastWantToInviteFireAt === 0` would otherwise get their schedule
  // pinned to an ancient `petCreatedAt + 10min` (already past), maturing
  // instantly on the next loop step. Returning `now` here keeps the
  // schedule on Path 2's stochastic anchor for catch-up users.
  const currentEvolution = Pet.getters.getCurrentEvolution(state);
  const isFirstPetBabyD0 =
    state.petRaised === 0 &&
    state.pet.evolutions.length === 1 &&
    currentEvolution?.stage === 'baby';
  if (!isFirstPetBabyD0) return now;

  const firstNudgeGate = duration({ minutes: 10 });
  // If pet is awake at creation (initial baby's babyAwakePeriod grace OR
  // wake hours), the gate clears at creation + 10 min wall-clock.
  // Otherwise the pet sleeps until JST wake-up first, then accrues
  // 10 min of awake-time from there.
  if (!shouldBeAsleep(state, petCreatedAt)) {
    return petCreatedAt + firstNudgeGate;
  }

  return petCreatedAt + getTimeUntilAwake(petCreatedAt) + firstNudgeGate;
};

/**
 * Detect if pet will want to go out with given input params
 */
export const shouldWantToGoOut = (
  state: State,
  random: number,
  now: number,
  features: Features,
) => {
  // Feature not unlocked yet.
  const { getLastVisitTimestamp, isUnlocked } = Visit.getters;
  if (!isUnlocked(state, features)) {
    return false;
  }

  const { getCurrentEvolution } = Pet.getters;

  // Pet not available
  const currentEvolution = getCurrentEvolution(state);
  if (!currentEvolution) {
    return false;
  }

  const lastVisitTimestamp = getLastVisitTimestamp(state);

  const timeSinceLastVisit = getAwakeDuration(state, lastVisitTimestamp, now);

  return Pet.ruleset.shouldWantToGoOut(timeSinceLastVisit, random);
};

/**
 * Retrieve the deterministic target stress value for sickness
 * @param state
 * @returns
 */
function getDeterministicSickStress(state: State) {
  const { getCurrentEvolution } = Pet.getters;
  const evolution = getCurrentEvolution(state);
  const stage = evolution?.stage;
  // Default to 100 if somehow the evolution or stage is not found
  if (!stage || !deterministicSickStress[stage]) return 100;
  // Return the target stress value for the given stage
  return deterministicSickStress[stage];
}

/**
 * Detect if pet will be sick with given input params
 */
export function shouldGetSick(
  state: State,
  random: number,
  now: number,
  since: number,
  stress: number,
): boolean {
  const isPetExist = !!Pet.getters.getPetCreatedAt(state);
  if (!isPetExist) return false;

  const denominator = 1000;

  const { lastUpdatedAt } = state.pet.statusEffects['sick'];
  const delta = now - lastUpdatedAt;

  // Do not get sick after two hours after previous sick
  const cooldown = sickCooldownInterval;

  if (lastUpdatedAt && delta < cooldown) return false;

  // If deterministic fate feature enabled, pet should get sick when stress value is 100
  if (features(state).deterministicFate) {
    const stress = getStressValue(state, now);
    return stress >= getDeterministicSickStress(state);
  }

  // Sick probability
  const probability = Math.max(stress - 30, 0) / denominator;
  const isSick = random < probability;

  return isSick;
}

/**
 * Detect if pet will have tooth decay with given input params
 */
export function shouldGetToothDecay(
  state: State,
  random: number,
  now: number,
  since: number,
): boolean {
  const { getPetCreatedAt, getAttributeValue } = Pet.getters;
  const isPetExist = !!getPetCreatedAt(state);
  if (!isPetExist) return false;

  const { lastUpdatedAt } = state.pet.statusEffects['toothDecay'];
  const delta = now - lastUpdatedAt;

  // Do not get tooth decay after two hours after previous tooth decay resolve
  if (lastUpdatedAt && delta < toothDecayCooldownInterval) return false;

  const toothAcidity = getAttributeValue(state, 'toothAcidity', now);
  const probability = Math.max(toothAcidity - 30, 0) / 1000;
  const hasToothDecay = random < probability;

  return hasToothDecay;
}

/**
 * Detect if pet will be scared with given input params
 */
export function shouldGetScared(
  state: State,
  random: number,
  now: number,
  since: number,
  attributes: Record<'love', number>,
): boolean {
  const { getCurrentEvolution, getPetCreatedAt } = Pet.getters;

  const isPetExist = !!getPetCreatedAt(state);
  if (!isPetExist) return false;

  const species = getCurrentEvolution(state)?.species;
  if (!species) return false;

  const love = {
    current: attributes.love,
    max: Pet.ruleset.attributeBounds.max,
  };

  const petSpeciesBucket = getPetSpeciesBucket(state);

  const { lastUpdatedAt } = state.pet.statusEffects['scared'];
  const delta = now - lastUpdatedAt;

  // Do not get scared after two hours after previous scared resolve
  if (lastUpdatedAt && delta < scaredCooldownInterval) return false;

  const coefficient = Pet.ruleset.scaredCoefficients[petSpeciesBucket][species];
  const loveDelta = love.max - love.current;
  const probability = (coefficient * Math.max(loveDelta - 30, 0)) / 1000;
  const isScared = random < probability;

  return isScared;
}

/**
 * Detect if pet will be evolving with given input params
 */
export function shouldEvolve(state: State, now: number): boolean {
  const { getCurrentEvolution } = Pet.getters;
  const lastEvolution = getCurrentEvolution(state);
  if (!lastEvolution) return false;

  const { timestamp, stage } = lastEvolution;

  // Don't evolve if evolution timestamp has been set in the future
  const { evolving } = state.pet.statusEffects;
  if (evolving.value && evolving.lastUpdatedAt > now) {
    return false;
  }

  // If time to evolve
  if (now - timestamp >= Pet.getters.getTimeToEvolve(state, stage)) {
    return true;
  }

  return false;
}

/**
 * Detect if we should get dying status
 *
 * While the pet is in sick status, determine if the pet will get dying or not based on the dying probability at 5 minute intervals
 * Dying probability is calculated by the formula below
 * Dying probability = stress / 1000 + pet_age / 100
 * For example, if stress value is 70 and the pet age is 3, the dying probability will be 70 / 1000 + 3 / 100 = 0.10 = 10%
 */
export function shouldGetDying(
  state: State,
  random: number,
  now: number,
  since: number,
  stress: number,
  features: Features,
): boolean {
  const isSick = hasPermanentStatusEffect(state, 'sick', now);

  // Only if pet sick
  if (!isSick) return false;

  // Detect dying moratorium
  const start = getStatusEffectStartTime(state, 'sick', now);
  const sleepingTime = getSleepingDuration(state, now, start); // Respect sleeping time
  const dyingMoratoriumInterval =
    Fate.ruleset.dyingMoratoriumInterval + sleepingTime;

  // Do not get dying after two hours after sick resolve
  if (start + dyingMoratoriumInterval > now) return false;

  // Keep dying cooldown
  const dyingCooldown = dyingCooldownInterval;

  // Do not get dying after three hours after previous dying resolve
  const dyingLastUpdatedAt = state.pet.statusEffects['dying'].lastUpdatedAt;
  const dyingDelta = now - dyingLastUpdatedAt;
  if (dyingLastUpdatedAt && dyingDelta < dyingCooldown) return false;

  // Keep sick cooldown
  // Do not get dying after three hours after previous sick resolve
  const sickLastUpdatedAt = state.pet.statusEffects['sick'].lastUpdatedAt;
  const sickDelta = now - sickLastUpdatedAt;
  if (sickLastUpdatedAt && sickDelta < dyingCooldown) return false;

  // If deterministic fate feature enabled, pet should be dying if sick for a fixed length of time
  if (features.deterministicFate) {
    // Sum sick start time with a fixed duration to get dying time
    const sickToDyingTime = start + duration({ minutes: 108 });
    // Should be dying if current time passes the fixed dying time
    return now >= sickToDyingTime;
  }

  // Calculate probability
  const denominator = 1000;
  const age = Pet.getters.getPetAge(state, now);
  const probability = Math.max(stress - 30, 0) / denominator + age / 500;

  // Detect if we hit probability
  if (probability > random) return true;
  return false;
}

/**
 * Detect if we should get dead status
 *
 * While the pet is in dying status, determine if the pet will die or not based on the death probability at 5 minute intervals
 * Death probability is calculated by the formula below
 * Death probability = stress / 1000 + pet_age / 100
 * For example, if stress value is 70 and the pet age is 3, the death probability will be 70 / 1000 + 3 / 100 = 0.10 = 10%
 */
export function shouldGetDead(
  state: State,
  random: number,
  now: number,
  since: number,
  stress: number,
  features: Features,
): boolean {
  // Only if pet dying
  const isDying = hasPermanentStatusEffect(state, 'dying', now);

  // Only if pet dying
  if (!isDying) return false;

  // Detect dead moratorium
  const start = getStatusEffectStartTime(state, 'dying', now);
  const sleepingTime = getSleepingDuration(state, now, start); // Respect sleeping time
  const deathMoratoriumInterval =
    Fate.ruleset.deadMoratoriumInterval + sleepingTime;

  // Do not get dead after six hours after dying resolve
  if (start + deathMoratoriumInterval > now) return false;

  // Pet can't die between 10pm and 9am or from 10pm and 10am
  const safePeriod = Fate.ruleset.safeTimePeriod;
  if (isSafeTime(now, safePeriod)) return false;

  // If deterministic fate feature enabled, pet should be dead if dying for a fixed length of time
  if (features.deterministicFate) {
    // Sum dying start time with a fixed duration to get dead time
    const dyingToDeadTime = start + duration({ minutes: 216 });
    // Should be dead if current time passes the fixed dead time
    return now >= dyingToDeadTime;
  }

  const age = Pet.getters.getPetAge(state, now);

  // Calculate probability
  const denominator = 2000;
  const probability = Math.max(stress - 30, 0) / denominator + age / 500;

  // Detect if we hit probability
  if (probability > random) return true;
  return false;
}

/**
 * Get remaining sleeping duration from now
 * NOTE: Assumes pet is currently asleep
 */
export function getTimeUntilAwake(now: number) {
  let awakeTime = now;
  let jstNow = getJSTDate(now);

  if (jstNow.getHours() >= sleepingPeriod.startHour) {
    awakeTime += duration({ hours: 24 - sleepingPeriod.startHour });
  }

  jstNow = getJSTDate(awakeTime);

  if (jstNow.getHours() < sleepingPeriod.endHour) {
    awakeTime += duration({
      hours: sleepingPeriod.endHour - jstNow.getHours(),
      minutes: -jstNow.getMinutes(),
      seconds: -jstNow.getSeconds(),
      ms: -jstNow.getMilliseconds(),
    });
  }

  return awakeTime - now;
}

/**
 * Get duration that pet has been asleep
 * NOTE: Assumes pet is currently asleep
 */
export function getTimeWhileAsleep(now: number) {
  let awakeTime = now;
  let jstNow = getJSTDate(now);

  if (jstNow.getHours() < sleepingPeriod.endHour) {
    awakeTime -= duration({ hours: sleepingPeriod.endHour });
  }

  jstNow = getJSTDate(awakeTime);

  if (jstNow.getHours() >= sleepingPeriod.startHour) {
    awakeTime -= duration({
      hours: jstNow.getHours() - sleepingPeriod.startHour,
      minutes: jstNow.getMinutes(),
      seconds: jstNow.getSeconds(),
      ms: jstNow.getMilliseconds(),
    });
  }

  return now - awakeTime;
}
